import React, {useEffect, useMemo, useState} from "react";
import {
    Box,
    Button,
    Flex,
    Grid,
    IconButton,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text
} from "@chakra-ui/react";
import {BiTrash} from "react-icons/bi";
import {v4 as uuidv4} from "uuid";
import BeaconAddressInput from "../../components/form/BeaconAddressInput";
import {BeaconTrigger, WiFiApTrigger} from "../../components/types";
import {isValidCueBeaconMac, normalizeCueBeaconTrigger, normalizeCueWiFiTrigger} from "./cueTriggerDialogUtils";

type Props = {
    isOpen: boolean;
    onClose: () => void;
    onSave: (next: {beaconTriggers: BeaconTrigger[]; wiFiTriggers: WiFiApTrigger[]}) => void;
    beaconTriggers: BeaconTrigger[];
    wiFiTriggers: WiFiApTrigger[];
    title: string;
    canEdit: boolean;
    allowBeaconTriggers?: boolean;
};

const CueTriggerEditorDialog = ({
    isOpen,
    onClose,
    onSave,
    beaconTriggers,
    wiFiTriggers,
    title,
    canEdit,
    allowBeaconTriggers = true
}: Props) => {
    const [draftBeaconTriggers, setDraftBeaconTriggers] = useState<BeaconTrigger[]>([]);
    const [draftWiFiTriggers, setDraftWiFiTriggers] = useState<WiFiApTrigger[]>([]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        setDraftBeaconTriggers((beaconTriggers || []).map((trigger) => ({...trigger})));
        setDraftWiFiTriggers((wiFiTriggers || []).map((trigger) => ({...trigger})));
    }, [beaconTriggers, isOpen, wiFiTriggers]);

    const hasInvalidBeacon = useMemo(() => {
        if (!allowBeaconTriggers) {
            return false;
        }
        return draftBeaconTriggers.some((trigger) => {
            const value = (trigger.macAddr || "").trim();
            if (!value) {
                return false;
            }
            return !isValidCueBeaconMac(value);
        });
    }, [allowBeaconTriggers, draftBeaconTriggers]);

    const addBeacon = () => {
        setDraftBeaconTriggers((previous) => ([
            ...previous,
            {
                id: uuidv4(),
                type: "Beacon",
                macAddr: "",
                majorId: "",
                minorId: ""
            }
        ]));
    };

    const addWiFi = () => {
        setDraftWiFiTriggers((previous) => ([
            ...previous,
            {
                id: uuidv4(),
                type: "WiFiAP",
                name: ""
            }
        ]));
    };

    const save = () => {
        const nextBeacons = allowBeaconTriggers
            ? draftBeaconTriggers
                .map((trigger) => normalizeCueBeaconTrigger(trigger))
                .filter((trigger): trigger is BeaconTrigger => !!trigger)
            : (beaconTriggers || []).map((trigger) => ({...trigger}));
        const nextWiFi = draftWiFiTriggers
            .map((trigger) => normalizeCueWiFiTrigger(trigger))
            .filter((trigger): trigger is WiFiApTrigger => !!trigger);
        onSave({
            beaconTriggers: nextBeacons,
            wiFiTriggers: nextWiFi
        });
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size={"3xl"}>
            <ModalOverlay/>
            <ModalContent>
                <ModalHeader>{title}</ModalHeader>
                <ModalCloseButton/>
                <ModalBody>
                    {allowBeaconTriggers ? (
                        <>
                            <Flex justify={"space-between"} align={"center"} mb={3}>
                                <Text fontWeight={"semibold"}>Beacon triggers</Text>
                                <Button size={"sm"} onClick={addBeacon} isDisabled={!canEdit}>Add Beacon</Button>
                            </Flex>
                            <Box>
                                {draftBeaconTriggers.length === 0 ? (
                                    <Text fontSize={"sm"} color={"gray.500"} mb={3}>No beacon triggers</Text>
                                ) : draftBeaconTriggers.map((trigger, index) => (
                                    <Grid key={`beacon-edit-${trigger.id}`} templateColumns={"max-content 150px max-content 140px max-content 1fr 36px"} columnGap={3} rowGap={2} mb={3} alignItems={"center"}>
                                        <Text fontSize={"sm"} color={"gray.600"}>Address</Text>
                                        <BeaconAddressInput
                                            beaconAddress={trigger.macAddr || ""}
                                            index={index}
                                            onBeaconAddressChange={(value, idx) => {
                                                setDraftBeaconTriggers((previous) => {
                                                    const next = [...previous];
                                                    next[idx] = {
                                                        ...next[idx],
                                                        macAddr: value
                                                    };
                                                    return next;
                                                });
                                            }}
                                        />
                                        <Text fontSize={"sm"} color={"gray.600"}>UUID</Text>
                                        <Input
                                            size={"sm"}
                                            value={trigger.uuid || ""}
                                            placeholder={"e.g. A1B2"}
                                            onChange={(event) => {
                                                const value = event.target.value.trim().toUpperCase();
                                                setDraftBeaconTriggers((previous) => {
                                                    const next = [...previous];
                                                    next[index] = {
                                                        ...next[index],
                                                        uuid: value.length > 0 ? value : undefined
                                                    };
                                                    return next;
                                                });
                                            }}
                                            isDisabled={!canEdit}
                                        />
                                        <Text fontSize={"sm"} color={"gray.600"}>Notes</Text>
                                        <Input
                                            size={"sm"}
                                            value={trigger.note || ""}
                                            placeholder={"Optional notes"}
                                            onChange={(event) => {
                                                const value = event.target.value;
                                                setDraftBeaconTriggers((previous) => {
                                                    const next = [...previous];
                                                    next[index] = {
                                                        ...next[index],
                                                        note: value
                                                    };
                                                    return next;
                                                });
                                            }}
                                            isDisabled={!canEdit}
                                        />
                                        <IconButton
                                            aria-label={"Delete beacon trigger"}
                                            icon={<BiTrash/>}
                                            size={"sm"}
                                            variant={"ghost"}
                                            onClick={() => {
                                                setDraftBeaconTriggers((previous) => previous.filter((item) => item.id !== trigger.id));
                                            }}
                                            isDisabled={!canEdit}
                                        />
                                    </Grid>
                                ))}
                            </Box>
                        </>
                    ) : null}

                    <Flex justify={"space-between"} align={"center"} mt={4} mb={3}>
                        <Text fontWeight={"semibold"}>WiFi AP triggers</Text>
                        <Button size={"sm"} onClick={addWiFi} isDisabled={!canEdit}>Add WiFi AP</Button>
                    </Flex>
                    <Box>
                        {draftWiFiTriggers.length === 0 ? (
                            <Text fontSize={"sm"} color={"gray.500"}>No WiFi AP triggers</Text>
                        ) : draftWiFiTriggers.map((trigger) => (
                            <Grid key={`wifi-edit-${trigger.id}`} templateColumns={"1fr 36px"} columnGap={2} rowGap={2} mb={2} alignItems={"center"}>
                                <Input
                                    size={"sm"}
                                    value={trigger.name || ""}
                                    placeholder={"WiFi network name"}
                                    onChange={(event) => {
                                        const value = event.target.value;
                                        setDraftWiFiTriggers((previous) => previous.map((item) => {
                                            if (item.id !== trigger.id) {
                                                return item;
                                            }
                                            return {
                                                ...item,
                                                name: value
                                            };
                                        }));
                                    }}
                                    isDisabled={!canEdit}
                                />
                                <IconButton
                                    aria-label={"Delete WiFi trigger"}
                                    icon={<BiTrash/>}
                                    size={"sm"}
                                    variant={"ghost"}
                                    onClick={() => {
                                        setDraftWiFiTriggers((previous) => previous.filter((item) => item.id !== trigger.id));
                                    }}
                                    isDisabled={!canEdit}
                                />
                            </Grid>
                        ))}
                    </Box>
                    {hasInvalidBeacon ? (
                        <Text mt={3} fontSize={"sm"} color={"red.500"}>
                            One or more beacon addresses are invalid and will not be saved.
                        </Text>
                    ) : null}
                </ModalBody>
                <ModalFooter>
                    <Button variant={"ghost"} mr={3} onClick={onClose}>Cancel</Button>
                    <Button colorScheme={"blue"} onClick={save} isDisabled={!canEdit}>Save</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default CueTriggerEditorDialog;
